/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteConnectionPathParameters,
  DisableConnectionPathParameters,
  EnableConnectionPathParameters,
  GetConnectionByIdParams,
  GetConnectionByIdPathParameters,
  GetConnectionKbDocsStatusPathParameters,
  GetConnectionsByConnectorParams,
  GetCredentialsByConnectionPathParameters,
  IngestConnectionDocsToKbPathParameters,
  ListSftpConnectionEntriesParams,
  ListSftpConnectionEntriesPathParameters,
  PurgeConnectionDocsFromKbPathParameters,
  TLConnectionCreatedResponse,
  TLConnectionCreateRequest,
  TLConnectionListResponse,
  TLConnectionResponse,
  TLConnectionSchemaListResponse,
  TLConnectionUpdateRequest,
  TLConnectorCredentialResponse,
  TLConnectorKbIngestResponse,
  TLHTTPValidationError,
  TLIngestConnectionDocsDataRequest,
  TLSftpBrowseResponse,
  TLSuccessResponse,
  UpdateConnectionPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Connections By Connector
 */
export const getConnectionsByConnector = (
  params: GetConnectionsByConnectorParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectionListResponse>({ url: `/data-connections`, method: 'GET', params, signal }, options);
};

export const getGetConnectionsByConnectorQueryKey = (params?: GetConnectionsByConnectorParams) => {
  return ['data-connections', ...(params ? [params] : [])] as const;
};

export const getGetConnectionsByConnectorQueryOptions = <
  TData = Awaited<ReturnType<typeof getConnectionsByConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetConnectionsByConnectorParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionsByConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConnectionsByConnectorQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConnectionsByConnector>>> = ({ signal }) =>
    getConnectionsByConnector(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getConnectionsByConnector>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetConnectionsByConnectorQueryResult = NonNullable<Awaited<ReturnType<typeof getConnectionsByConnector>>>;
export type GetConnectionsByConnectorQueryError = ErrorType<TLHTTPValidationError>;

export function useGetConnectionsByConnector<
  TData = Awaited<ReturnType<typeof getConnectionsByConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetConnectionsByConnectorParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionsByConnector>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectionsByConnector>>,
          TError,
          Awaited<ReturnType<typeof getConnectionsByConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectionsByConnector<
  TData = Awaited<ReturnType<typeof getConnectionsByConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetConnectionsByConnectorParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionsByConnector>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectionsByConnector>>,
          TError,
          Awaited<ReturnType<typeof getConnectionsByConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectionsByConnector<
  TData = Awaited<ReturnType<typeof getConnectionsByConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetConnectionsByConnectorParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionsByConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Connections By Connector
 */

export function useGetConnectionsByConnector<
  TData = Awaited<ReturnType<typeof getConnectionsByConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetConnectionsByConnectorParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionsByConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConnectionsByConnectorQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Connections By Connector
 */
export const prefetchGetConnectionsByConnectorQuery = async <
  TData = Awaited<ReturnType<typeof getConnectionsByConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: GetConnectionsByConnectorParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionsByConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConnectionsByConnectorQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Connections By Connector
 */
export const invalidateGetConnectionsByConnector = async (
  queryClient: QueryClient,
  params: GetConnectionsByConnectorParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConnectionsByConnectorQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Connection
 */
export const createConnection = (
  tLConnectionCreateRequest: BodyType<TLConnectionCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectionCreatedResponse>(
    {
      url: `/data-connections`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLConnectionCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateConnectionQueryKey = (tLConnectionCreateRequest?: BodyType<TLConnectionCreateRequest>) => {
  return ['POST', 'data-connections', tLConnectionCreateRequest] as const;
};

export const getCreateConnectionQueryOptions = <
  TData = Awaited<ReturnType<typeof createConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectionCreateRequest: BodyType<TLConnectionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateConnectionQueryKey(tLConnectionCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createConnection>>> = ({ signal }) =>
    createConnection(tLConnectionCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createConnection>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof createConnection>>>;
export type CreateConnectionQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateConnection<
  TData = Awaited<ReturnType<typeof createConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectionCreateRequest: BodyType<TLConnectionCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnection>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConnection>>,
          TError,
          Awaited<ReturnType<typeof createConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConnection<
  TData = Awaited<ReturnType<typeof createConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectionCreateRequest: BodyType<TLConnectionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnection>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConnection>>,
          TError,
          Awaited<ReturnType<typeof createConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConnection<
  TData = Awaited<ReturnType<typeof createConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectionCreateRequest: BodyType<TLConnectionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Connection
 */

export function useCreateConnection<
  TData = Awaited<ReturnType<typeof createConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectionCreateRequest: BodyType<TLConnectionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateConnectionQueryOptions(tLConnectionCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Connection
 */
export const prefetchCreateConnectionQuery = async <
  TData = Awaited<ReturnType<typeof createConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLConnectionCreateRequest: BodyType<TLConnectionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateConnectionQueryOptions(tLConnectionCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Connection
 */
export const invalidateCreateConnection = async (
  queryClient: QueryClient,
  tLConnectionCreateRequest: BodyType<TLConnectionCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateConnectionQueryKey(tLConnectionCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary Get Connection Schemas
 */
export const getConnectionSchemas = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLConnectionSchemaListResponse>(
    { url: `/data-connections/schema`, method: 'GET', signal },
    options,
  );
};

export const getGetConnectionSchemasQueryKey = () => {
  return ['data-connections', 'schema'] as const;
};

export const getGetConnectionSchemasQueryOptions = <
  TData = Awaited<ReturnType<typeof getConnectionSchemas>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionSchemas>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConnectionSchemasQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConnectionSchemas>>> = ({ signal }) =>
    getConnectionSchemas(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getConnectionSchemas>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetConnectionSchemasQueryResult = NonNullable<Awaited<ReturnType<typeof getConnectionSchemas>>>;
export type GetConnectionSchemasQueryError = ErrorType<unknown>;

export function useGetConnectionSchemas<
  TData = Awaited<ReturnType<typeof getConnectionSchemas>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionSchemas>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectionSchemas>>,
          TError,
          Awaited<ReturnType<typeof getConnectionSchemas>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectionSchemas<
  TData = Awaited<ReturnType<typeof getConnectionSchemas>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionSchemas>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectionSchemas>>,
          TError,
          Awaited<ReturnType<typeof getConnectionSchemas>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectionSchemas<
  TData = Awaited<ReturnType<typeof getConnectionSchemas>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Connection Schemas
 */

export function useGetConnectionSchemas<
  TData = Awaited<ReturnType<typeof getConnectionSchemas>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConnectionSchemasQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Connection Schemas
 */
export const prefetchGetConnectionSchemasQuery = async <
  TData = Awaited<ReturnType<typeof getConnectionSchemas>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConnectionSchemasQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Connection Schemas
 */
export const invalidateGetConnectionSchemas = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConnectionSchemasQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Delete Connection
 */
export const deleteConnection = (
  { connectionId }: DeleteConnectionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/data-connections/${connectionId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteConnectionQueryKey = ({ connectionId }: DeleteConnectionPathParameters) => {
  return ['DELETE', 'data-connections', connectionId] as const;
};

export const getDeleteConnectionQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: DeleteConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteConnectionQueryKey({ connectionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteConnection>>> = ({ signal }) =>
    deleteConnection({ connectionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteConnection>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof deleteConnection>>>;
export type DeleteConnectionQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteConnection<
  TData = Awaited<ReturnType<typeof deleteConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConnectionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnection>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConnection>>,
          TError,
          Awaited<ReturnType<typeof deleteConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConnection<
  TData = Awaited<ReturnType<typeof deleteConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnection>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConnection>>,
          TError,
          Awaited<ReturnType<typeof deleteConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConnection<
  TData = Awaited<ReturnType<typeof deleteConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Connection
 */

export function useDeleteConnection<
  TData = Awaited<ReturnType<typeof deleteConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: DeleteConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteConnectionQueryOptions({ connectionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Connection
 */
export const prefetchDeleteConnectionQuery = async <
  TData = Awaited<ReturnType<typeof deleteConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: DeleteConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteConnectionQueryOptions({ connectionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Connection
 */
export const invalidateDeleteConnection = async (
  queryClient: QueryClient,
  { connectionId }: DeleteConnectionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteConnectionQueryKey({ connectionId }) }, options);

  return queryClient;
};

/**
 * @summary Get Connection By Id
 */
export const getConnectionById = (
  { connectionId }: GetConnectionByIdPathParameters,
  params?: GetConnectionByIdParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectionResponse>(
    { url: `/data-connections/${connectionId}`, method: 'GET', params, signal },
    options,
  );
};

export const getGetConnectionByIdQueryKey = (
  { connectionId }: GetConnectionByIdPathParameters,
  params?: GetConnectionByIdParams,
) => {
  return ['data-connections', connectionId, ...(params ? [params] : [])] as const;
};

export const getGetConnectionByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getConnectionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: GetConnectionByIdPathParameters,
  params?: GetConnectionByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConnectionByIdQueryKey({ connectionId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConnectionById>>> = ({ signal }) =>
    getConnectionById({ connectionId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getConnectionById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetConnectionByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getConnectionById>>>;
export type GetConnectionByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetConnectionById<
  TData = Awaited<ReturnType<typeof getConnectionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConnectionByIdPathParameters,
  params: undefined | GetConnectionByIdParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectionById>>,
          TError,
          Awaited<ReturnType<typeof getConnectionById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectionById<
  TData = Awaited<ReturnType<typeof getConnectionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConnectionByIdPathParameters,
  params?: GetConnectionByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectionById>>,
          TError,
          Awaited<ReturnType<typeof getConnectionById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectionById<
  TData = Awaited<ReturnType<typeof getConnectionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConnectionByIdPathParameters,
  params?: GetConnectionByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Connection By Id
 */

export function useGetConnectionById<
  TData = Awaited<ReturnType<typeof getConnectionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: GetConnectionByIdPathParameters,
  params?: GetConnectionByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConnectionByIdQueryOptions({ connectionId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Connection By Id
 */
export const prefetchGetConnectionByIdQuery = async <
  TData = Awaited<ReturnType<typeof getConnectionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: GetConnectionByIdPathParameters,
  params?: GetConnectionByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConnectionByIdQueryOptions({ connectionId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Connection By Id
 */
export const invalidateGetConnectionById = async (
  queryClient: QueryClient,
  { connectionId }: GetConnectionByIdPathParameters,
  params?: GetConnectionByIdParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConnectionByIdQueryKey({ connectionId }, params) }, options);

  return queryClient;
};

/**
 * @summary Update Connection
 */
export const updateConnection = (
  { connectionId }: UpdateConnectionPathParameters,
  tLConnectionUpdateRequest: BodyType<TLConnectionUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/data-connections/${connectionId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLConnectionUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateConnectionQueryKey = (
  { connectionId }: UpdateConnectionPathParameters,
  tLConnectionUpdateRequest?: BodyType<TLConnectionUpdateRequest>,
) => {
  return ['PATCH', 'data-connections', connectionId, tLConnectionUpdateRequest] as const;
};

export const getUpdateConnectionQueryOptions = <
  TData = Awaited<ReturnType<typeof updateConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: UpdateConnectionPathParameters,
  tLConnectionUpdateRequest: BodyType<TLConnectionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateConnectionQueryKey({ connectionId }, tLConnectionUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateConnection>>> = ({ signal }) =>
    updateConnection({ connectionId }, tLConnectionUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateConnection>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof updateConnection>>>;
export type UpdateConnectionQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateConnection<
  TData = Awaited<ReturnType<typeof updateConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConnectionPathParameters,
  tLConnectionUpdateRequest: BodyType<TLConnectionUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnection>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateConnection>>,
          TError,
          Awaited<ReturnType<typeof updateConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateConnection<
  TData = Awaited<ReturnType<typeof updateConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConnectionPathParameters,
  tLConnectionUpdateRequest: BodyType<TLConnectionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnection>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateConnection>>,
          TError,
          Awaited<ReturnType<typeof updateConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateConnection<
  TData = Awaited<ReturnType<typeof updateConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConnectionPathParameters,
  tLConnectionUpdateRequest: BodyType<TLConnectionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Connection
 */

export function useUpdateConnection<
  TData = Awaited<ReturnType<typeof updateConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: UpdateConnectionPathParameters,
  tLConnectionUpdateRequest: BodyType<TLConnectionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateConnectionQueryOptions({ connectionId }, tLConnectionUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Connection
 */
export const prefetchUpdateConnectionQuery = async <
  TData = Awaited<ReturnType<typeof updateConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: UpdateConnectionPathParameters,
  tLConnectionUpdateRequest: BodyType<TLConnectionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateConnectionQueryOptions({ connectionId }, tLConnectionUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Connection
 */
export const invalidateUpdateConnection = async (
  queryClient: QueryClient,
  { connectionId }: UpdateConnectionPathParameters,
  tLConnectionUpdateRequest: BodyType<TLConnectionUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateConnectionQueryKey({ connectionId }, tLConnectionUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Credentials By Connection
 */
export const getCredentialsByConnection = (
  { connectionId }: GetCredentialsByConnectionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectorCredentialResponse[]>(
    { url: `/data-connections/${connectionId}/credentials`, method: 'GET', signal },
    options,
  );
};

export const getGetCredentialsByConnectionQueryKey = ({ connectionId }: GetCredentialsByConnectionPathParameters) => {
  return ['data-connections', connectionId, 'credentials'] as const;
};

export const getGetCredentialsByConnectionQueryOptions = <
  TData = Awaited<ReturnType<typeof getCredentialsByConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: GetCredentialsByConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialsByConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCredentialsByConnectionQueryKey({ connectionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCredentialsByConnection>>> = ({ signal }) =>
    getCredentialsByConnection({ connectionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCredentialsByConnection>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetCredentialsByConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof getCredentialsByConnection>>>;
export type GetCredentialsByConnectionQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCredentialsByConnection<
  TData = Awaited<ReturnType<typeof getCredentialsByConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCredentialsByConnectionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialsByConnection>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCredentialsByConnection>>,
          TError,
          Awaited<ReturnType<typeof getCredentialsByConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCredentialsByConnection<
  TData = Awaited<ReturnType<typeof getCredentialsByConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCredentialsByConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialsByConnection>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCredentialsByConnection>>,
          TError,
          Awaited<ReturnType<typeof getCredentialsByConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCredentialsByConnection<
  TData = Awaited<ReturnType<typeof getCredentialsByConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCredentialsByConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialsByConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Credentials By Connection
 */

export function useGetCredentialsByConnection<
  TData = Awaited<ReturnType<typeof getCredentialsByConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: GetCredentialsByConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialsByConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCredentialsByConnectionQueryOptions({ connectionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Credentials By Connection
 */
export const prefetchGetCredentialsByConnectionQuery = async <
  TData = Awaited<ReturnType<typeof getCredentialsByConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: GetCredentialsByConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialsByConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCredentialsByConnectionQueryOptions({ connectionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Credentials By Connection
 */
export const invalidateGetCredentialsByConnection = async (
  queryClient: QueryClient,
  { connectionId }: GetCredentialsByConnectionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCredentialsByConnectionQueryKey({ connectionId }) }, options);

  return queryClient;
};

/**
 * @summary Disable Connection
 */
export const disableConnection = (
  { connectionId }: DisableConnectionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/data-connections/${connectionId}/disable`, method: 'PATCH', signal },
    options,
  );
};

export const getDisableConnectionQueryKey = ({ connectionId }: DisableConnectionPathParameters) => {
  return ['PATCH', 'data-connections', connectionId, 'disable'] as const;
};

export const getDisableConnectionQueryOptions = <
  TData = Awaited<ReturnType<typeof disableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: DisableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDisableConnectionQueryKey({ connectionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof disableConnection>>> = ({ signal }) =>
    disableConnection({ connectionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof disableConnection>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DisableConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof disableConnection>>>;
export type DisableConnectionQueryError = ErrorType<TLHTTPValidationError>;

export function useDisableConnection<
  TData = Awaited<ReturnType<typeof disableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DisableConnectionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnection>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof disableConnection>>,
          TError,
          Awaited<ReturnType<typeof disableConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDisableConnection<
  TData = Awaited<ReturnType<typeof disableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DisableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnection>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof disableConnection>>,
          TError,
          Awaited<ReturnType<typeof disableConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDisableConnection<
  TData = Awaited<ReturnType<typeof disableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DisableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Disable Connection
 */

export function useDisableConnection<
  TData = Awaited<ReturnType<typeof disableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: DisableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDisableConnectionQueryOptions({ connectionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Disable Connection
 */
export const prefetchDisableConnectionQuery = async <
  TData = Awaited<ReturnType<typeof disableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: DisableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDisableConnectionQueryOptions({ connectionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Disable Connection
 */
export const invalidateDisableConnection = async (
  queryClient: QueryClient,
  { connectionId }: DisableConnectionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDisableConnectionQueryKey({ connectionId }) }, options);

  return queryClient;
};

/**
 * @summary Enable Connection
 */
export const enableConnection = (
  { connectionId }: EnableConnectionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/data-connections/${connectionId}/enable`, method: 'PATCH', signal },
    options,
  );
};

export const getEnableConnectionQueryKey = ({ connectionId }: EnableConnectionPathParameters) => {
  return ['PATCH', 'data-connections', connectionId, 'enable'] as const;
};

export const getEnableConnectionQueryOptions = <
  TData = Awaited<ReturnType<typeof enableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: EnableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getEnableConnectionQueryKey({ connectionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof enableConnection>>> = ({ signal }) =>
    enableConnection({ connectionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof enableConnection>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type EnableConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof enableConnection>>>;
export type EnableConnectionQueryError = ErrorType<TLHTTPValidationError>;

export function useEnableConnection<
  TData = Awaited<ReturnType<typeof enableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: EnableConnectionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnection>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof enableConnection>>,
          TError,
          Awaited<ReturnType<typeof enableConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEnableConnection<
  TData = Awaited<ReturnType<typeof enableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: EnableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnection>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof enableConnection>>,
          TError,
          Awaited<ReturnType<typeof enableConnection>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEnableConnection<
  TData = Awaited<ReturnType<typeof enableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: EnableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Enable Connection
 */

export function useEnableConnection<
  TData = Awaited<ReturnType<typeof enableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: EnableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getEnableConnectionQueryOptions({ connectionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Enable Connection
 */
export const prefetchEnableConnectionQuery = async <
  TData = Awaited<ReturnType<typeof enableConnection>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: EnableConnectionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnection>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getEnableConnectionQueryOptions({ connectionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Enable Connection
 */
export const invalidateEnableConnection = async (
  queryClient: QueryClient,
  { connectionId }: EnableConnectionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getEnableConnectionQueryKey({ connectionId }) }, options);

  return queryClient;
};

/**
 * @summary Purge Connection Docs From Kb
 */
export const purgeConnectionDocsFromKb = (
  { connectionId }: PurgeConnectionDocsFromKbPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/data-connections/${connectionId}/kb-ingest`, method: 'DELETE', signal },
    options,
  );
};

export const getPurgeConnectionDocsFromKbQueryKey = ({ connectionId }: PurgeConnectionDocsFromKbPathParameters) => {
  return ['DELETE', 'data-connections', connectionId, 'kb-ingest'] as const;
};

export const getPurgeConnectionDocsFromKbQueryOptions = <
  TData = Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: PurgeConnectionDocsFromKbPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPurgeConnectionDocsFromKbQueryKey({ connectionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>> = ({ signal }) =>
    purgeConnectionDocsFromKb({ connectionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type PurgeConnectionDocsFromKbQueryResult = NonNullable<Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>>;
export type PurgeConnectionDocsFromKbQueryError = ErrorType<TLHTTPValidationError>;

export function usePurgeConnectionDocsFromKb<
  TData = Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PurgeConnectionDocsFromKbPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>,
          TError,
          Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePurgeConnectionDocsFromKb<
  TData = Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PurgeConnectionDocsFromKbPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>,
          TError,
          Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePurgeConnectionDocsFromKb<
  TData = Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PurgeConnectionDocsFromKbPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Purge Connection Docs From Kb
 */

export function usePurgeConnectionDocsFromKb<
  TData = Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: PurgeConnectionDocsFromKbPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPurgeConnectionDocsFromKbQueryOptions({ connectionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Purge Connection Docs From Kb
 */
export const prefetchPurgeConnectionDocsFromKbQuery = async <
  TData = Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: PurgeConnectionDocsFromKbPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeConnectionDocsFromKb>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPurgeConnectionDocsFromKbQueryOptions({ connectionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Purge Connection Docs From Kb
 */
export const invalidatePurgeConnectionDocsFromKb = async (
  queryClient: QueryClient,
  { connectionId }: PurgeConnectionDocsFromKbPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getPurgeConnectionDocsFromKbQueryKey({ connectionId }) }, options);

  return queryClient;
};

/**
 * @summary Get Connection Kb Docs Status
 */
export const getConnectionKbDocsStatus = (
  { connectionId }: GetConnectionKbDocsStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectorKbIngestResponse | null>(
    { url: `/data-connections/${connectionId}/kb-ingest`, method: 'GET', signal },
    options,
  );
};

export const getGetConnectionKbDocsStatusQueryKey = ({ connectionId }: GetConnectionKbDocsStatusPathParameters) => {
  return ['data-connections', connectionId, 'kb-ingest'] as const;
};

export const getGetConnectionKbDocsStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getConnectionKbDocsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: GetConnectionKbDocsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionKbDocsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConnectionKbDocsStatusQueryKey({ connectionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConnectionKbDocsStatus>>> = ({ signal }) =>
    getConnectionKbDocsStatus({ connectionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getConnectionKbDocsStatus>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetConnectionKbDocsStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getConnectionKbDocsStatus>>>;
export type GetConnectionKbDocsStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetConnectionKbDocsStatus<
  TData = Awaited<ReturnType<typeof getConnectionKbDocsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConnectionKbDocsStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionKbDocsStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectionKbDocsStatus>>,
          TError,
          Awaited<ReturnType<typeof getConnectionKbDocsStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectionKbDocsStatus<
  TData = Awaited<ReturnType<typeof getConnectionKbDocsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConnectionKbDocsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionKbDocsStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectionKbDocsStatus>>,
          TError,
          Awaited<ReturnType<typeof getConnectionKbDocsStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectionKbDocsStatus<
  TData = Awaited<ReturnType<typeof getConnectionKbDocsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConnectionKbDocsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionKbDocsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Connection Kb Docs Status
 */

export function useGetConnectionKbDocsStatus<
  TData = Awaited<ReturnType<typeof getConnectionKbDocsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: GetConnectionKbDocsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionKbDocsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConnectionKbDocsStatusQueryOptions({ connectionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Connection Kb Docs Status
 */
export const prefetchGetConnectionKbDocsStatusQuery = async <
  TData = Awaited<ReturnType<typeof getConnectionKbDocsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: GetConnectionKbDocsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectionKbDocsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConnectionKbDocsStatusQueryOptions({ connectionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Connection Kb Docs Status
 */
export const invalidateGetConnectionKbDocsStatus = async (
  queryClient: QueryClient,
  { connectionId }: GetConnectionKbDocsStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConnectionKbDocsStatusQueryKey({ connectionId }) }, options);

  return queryClient;
};

/**
 * @summary Ingest Connection Docs To Kb
 */
export const ingestConnectionDocsToKb = (
  { connectionId }: IngestConnectionDocsToKbPathParameters,
  tLIngestConnectionDocsDataRequest?: BodyType<TLIngestConnectionDocsDataRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  if (
    tLIngestConnectionDocsDataRequest?.docs_text !== undefined &&
    tLIngestConnectionDocsDataRequest.docs_text !== null
  ) {
    formData.append(`docs_text`, tLIngestConnectionDocsDataRequest.docs_text);
  }
  if (tLIngestConnectionDocsDataRequest?.files !== undefined) {
    tLIngestConnectionDocsDataRequest?.files.forEach((value) => formData.append(`files`, value));
  }

  return apiInstance<TLConnectorKbIngestResponse>(
    {
      url: `/data-connections/${connectionId}/kb-ingest`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      signal,
    },
    options,
  );
};

export const getIngestConnectionDocsToKbQueryKey = (
  { connectionId }: IngestConnectionDocsToKbPathParameters,
  tLIngestConnectionDocsDataRequest?: BodyType<TLIngestConnectionDocsDataRequest>,
) => {
  return ['POST', 'data-connections', connectionId, 'kb-ingest', tLIngestConnectionDocsDataRequest] as const;
};

export const getIngestConnectionDocsToKbQueryOptions = <
  TData = Awaited<ReturnType<typeof ingestConnectionDocsToKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: IngestConnectionDocsToKbPathParameters,
  tLIngestConnectionDocsDataRequest?: BodyType<TLIngestConnectionDocsDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof ingestConnectionDocsToKb>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getIngestConnectionDocsToKbQueryKey({ connectionId }, tLIngestConnectionDocsDataRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof ingestConnectionDocsToKb>>> = ({ signal }) =>
    ingestConnectionDocsToKb({ connectionId }, tLIngestConnectionDocsDataRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof ingestConnectionDocsToKb>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type IngestConnectionDocsToKbQueryResult = NonNullable<Awaited<ReturnType<typeof ingestConnectionDocsToKb>>>;
export type IngestConnectionDocsToKbQueryError = ErrorType<TLHTTPValidationError>;

export function useIngestConnectionDocsToKb<
  TData = Awaited<ReturnType<typeof ingestConnectionDocsToKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: IngestConnectionDocsToKbPathParameters,
  tLIngestConnectionDocsDataRequest: undefined | BodyType<TLIngestConnectionDocsDataRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof ingestConnectionDocsToKb>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof ingestConnectionDocsToKb>>,
          TError,
          Awaited<ReturnType<typeof ingestConnectionDocsToKb>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useIngestConnectionDocsToKb<
  TData = Awaited<ReturnType<typeof ingestConnectionDocsToKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: IngestConnectionDocsToKbPathParameters,
  tLIngestConnectionDocsDataRequest?: BodyType<TLIngestConnectionDocsDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof ingestConnectionDocsToKb>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof ingestConnectionDocsToKb>>,
          TError,
          Awaited<ReturnType<typeof ingestConnectionDocsToKb>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useIngestConnectionDocsToKb<
  TData = Awaited<ReturnType<typeof ingestConnectionDocsToKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: IngestConnectionDocsToKbPathParameters,
  tLIngestConnectionDocsDataRequest?: BodyType<TLIngestConnectionDocsDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof ingestConnectionDocsToKb>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Ingest Connection Docs To Kb
 */

export function useIngestConnectionDocsToKb<
  TData = Awaited<ReturnType<typeof ingestConnectionDocsToKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: IngestConnectionDocsToKbPathParameters,
  tLIngestConnectionDocsDataRequest?: BodyType<TLIngestConnectionDocsDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof ingestConnectionDocsToKb>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getIngestConnectionDocsToKbQueryOptions(
    { connectionId },
    tLIngestConnectionDocsDataRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Ingest Connection Docs To Kb
 */
export const prefetchIngestConnectionDocsToKbQuery = async <
  TData = Awaited<ReturnType<typeof ingestConnectionDocsToKb>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: IngestConnectionDocsToKbPathParameters,
  tLIngestConnectionDocsDataRequest?: BodyType<TLIngestConnectionDocsDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof ingestConnectionDocsToKb>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getIngestConnectionDocsToKbQueryOptions(
    { connectionId },
    tLIngestConnectionDocsDataRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Ingest Connection Docs To Kb
 */
export const invalidateIngestConnectionDocsToKb = async (
  queryClient: QueryClient,
  { connectionId }: IngestConnectionDocsToKbPathParameters,
  tLIngestConnectionDocsDataRequest?: BodyType<TLIngestConnectionDocsDataRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getIngestConnectionDocsToKbQueryKey({ connectionId }, tLIngestConnectionDocsDataRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary List Sftp Connection Entries
 */
export const listSftpConnectionEntries = (
  { connectionId }: ListSftpConnectionEntriesPathParameters,
  params?: ListSftpConnectionEntriesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSftpBrowseResponse>(
    { url: `/data-connections/${connectionId}/sftp/entries`, method: 'GET', params, signal },
    options,
  );
};

export const getListSftpConnectionEntriesQueryKey = (
  { connectionId }: ListSftpConnectionEntriesPathParameters,
  params?: ListSftpConnectionEntriesParams,
) => {
  return ['data-connections', connectionId, 'sftp', 'entries', ...(params ? [params] : [])] as const;
};

export const getListSftpConnectionEntriesQueryOptions = <
  TData = Awaited<ReturnType<typeof listSftpConnectionEntries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: ListSftpConnectionEntriesPathParameters,
  params?: ListSftpConnectionEntriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSftpConnectionEntries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSftpConnectionEntriesQueryKey({ connectionId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSftpConnectionEntries>>> = ({ signal }) =>
    listSftpConnectionEntries({ connectionId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listSftpConnectionEntries>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListSftpConnectionEntriesQueryResult = NonNullable<Awaited<ReturnType<typeof listSftpConnectionEntries>>>;
export type ListSftpConnectionEntriesQueryError = ErrorType<TLHTTPValidationError>;

export function useListSftpConnectionEntries<
  TData = Awaited<ReturnType<typeof listSftpConnectionEntries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListSftpConnectionEntriesPathParameters,
  params: undefined | ListSftpConnectionEntriesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSftpConnectionEntries>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSftpConnectionEntries>>,
          TError,
          Awaited<ReturnType<typeof listSftpConnectionEntries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSftpConnectionEntries<
  TData = Awaited<ReturnType<typeof listSftpConnectionEntries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListSftpConnectionEntriesPathParameters,
  params?: ListSftpConnectionEntriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSftpConnectionEntries>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSftpConnectionEntries>>,
          TError,
          Awaited<ReturnType<typeof listSftpConnectionEntries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSftpConnectionEntries<
  TData = Awaited<ReturnType<typeof listSftpConnectionEntries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListSftpConnectionEntriesPathParameters,
  params?: ListSftpConnectionEntriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSftpConnectionEntries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Sftp Connection Entries
 */

export function useListSftpConnectionEntries<
  TData = Awaited<ReturnType<typeof listSftpConnectionEntries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectionId }: ListSftpConnectionEntriesPathParameters,
  params?: ListSftpConnectionEntriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSftpConnectionEntries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSftpConnectionEntriesQueryOptions({ connectionId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Sftp Connection Entries
 */
export const prefetchListSftpConnectionEntriesQuery = async <
  TData = Awaited<ReturnType<typeof listSftpConnectionEntries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectionId }: ListSftpConnectionEntriesPathParameters,
  params?: ListSftpConnectionEntriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSftpConnectionEntries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListSftpConnectionEntriesQueryOptions({ connectionId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Sftp Connection Entries
 */
export const invalidateListSftpConnectionEntries = async (
  queryClient: QueryClient,
  { connectionId }: ListSftpConnectionEntriesPathParameters,
  params?: ListSftpConnectionEntriesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListSftpConnectionEntriesQueryKey({ connectionId }, params) },
    options,
  );

  return queryClient;
};
